"use client";

import { c as _c } from "react/compiler-runtime";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropId } from "@/generated/models/ApiDropId";
import type { Drop } from "@/helpers/waves/drop.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import useDmWavesList from "@/hooks/useDmWavesList";
import useWavesList from "@/hooks/useWavesList";
import { WebSocketStatus } from "@/services/websocket/WebSocketTypes";
import { logWebSocketDebug } from "@/services/websocket/webSocketDebug";
import { useWebsocketStatus } from "@/services/websocket/useWebSocketMessage";
import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { WaveMessages } from "./hooks/types";
import { useActiveWaveManager } from "./hooks/useActiveWaveManager";
import type { MinimalWave } from "./hooks/useEnhancedWavesListCore";
import useEnhancedWavesListCore from "./hooks/useEnhancedWavesListCore";
import { useWaveDataManager } from "./hooks/useWaveDataManager";
import type { Listener as WaveMessagesListener } from "./hooks/useWaveMessagesStore";
import useWaveMessagesStore from "./hooks/useWaveMessagesStore";
import type { NextPageProps } from "./hooks/useWavePagination";
import type { ProcessIncomingDropType } from "./hooks/useWaveRealtimeUpdater";
import { useWaveRealtimeUpdater } from "./hooks/useWaveRealtimeUpdater";

// Define nested structures for context data
interface WavesContextData {
  readonly list: MinimalWave[];
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly addPinnedWave: (id: string) => void;
  readonly removePinnedWave: (id: string) => void;
  readonly restoreWaveUnreadCount: (waveId: string, count?: number) => void;
}
interface ActiveWaveContextData {
  readonly id: string | null;
  readonly set: (waveId: string | null, options?: {
    isDirectMessage?: boolean | undefined;
    replace?: boolean | undefined;
    serialNo?: number | string | null | undefined;
    divider?: number | null | undefined;
  }) => void;
}

// Define the interface for the wave messages store functions
interface WaveMessagesStoreData {
  readonly getData: (key: string) => WaveMessages | undefined;
  readonly subscribe: (key: string, listener: WaveMessagesListener) => void;
  readonly unsubscribe: (key: string, listener: WaveMessagesListener) => void;
}

// Define the type for our context using nested structures
interface MyStreamContextType {
  readonly waves: WavesContextData;
  readonly directMessages: WavesContextData;
  readonly activeWave: ActiveWaveContextData;
  readonly waveMessagesStore: WaveMessagesStoreData;
  readonly registerWave: (waveId: string, syncNewest?: boolean) => void;
  readonly fetchNextPageForWave: (props: NextPageProps) => Promise<(ApiDrop | ApiDropId)[] | null>;
  readonly fetchAroundSerialNo: (waveId: string, serialNo: number) => void;
  readonly processIncomingDrop: (drop: ApiDrop, type: ProcessIncomingDropType) => void;
  readonly processDropRemoved: (waveId: string, dropId: string) => void;
  readonly applyOptimisticDropUpdate: ({
    waveId,
    dropId,
    update
  }: {
    waveId: string;
    dropId: string;
    update: (draft: Drop) => Drop | void;
  }) => {
    rollback: () => void;
  } | null;
}
interface MyStreamProviderProps {
  readonly children: ReactNode;
}
const BROWSER_RESUME_SYNC_COOLDOWN_MS = 1000;
type StreamSyncSource = "browser-resume" | "websocket-connected" | "capacitor-resume";
type BrowserResumeSource = "visibilitychange" | "focus";

// Create the context
const MyStreamContext = createContext<MyStreamContextType | null>(null);

// Create a provider component
export const MyStreamProvider: React.FC<MyStreamProviderProps> = t0 => {
  const $ = _c(101);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isActive
  } = useCapacitor();
  const {
    activeWaveId,
    setActiveWave
  } = useActiveWaveManager();
  const mainWavesData = useWavesList();
  const dmWavesData = useDmWavesList();
  let t1;
  if ($[0] !== mainWavesData.waves) {
    t1 = new Set(mainWavesData.waves.map(_temp));
    $[0] = mainWavesData.waves;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mainWaveIds = t1;
  let t2;
  if ($[2] !== dmWavesData.waves) {
    t2 = new Set(dmWavesData.waves.map(_temp2));
    $[2] = dmWavesData.waves;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dmWaveIds = t2;
  let t3;
  if ($[4] !== dmWaveIds) {
    t3 = {
      supportsPinning: true,
      otherListWaveIds: dmWaveIds
    };
    $[4] = dmWaveIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const wavesHookData = useEnhancedWavesListCore(activeWaveId, mainWavesData, t3);
  let t4;
  if ($[6] !== mainWaveIds) {
    t4 = {
      supportsPinning: false,
      otherListWaveIds: mainWaveIds
    };
    $[6] = mainWaveIds;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const dmWavesHookData = useEnhancedWavesListCore(activeWaveId, dmWavesData, t4);
  const waveMessagesStore = useWaveMessagesStore();
  const websocketStatus = useWebsocketStatus();
  const prevIsActiveRef = useRef(isActive);
  const lastBrowserResumeSyncAtRef = useRef(0);
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  let t5;
  if ($[8] !== waveMessagesStore.getData || $[9] !== waveMessagesStore.removeDrop || $[10] !== waveMessagesStore.updateData) {
    t5 = {
      updateData: waveMessagesStore.updateData,
      getData: waveMessagesStore.getData,
      removeDrop: waveMessagesStore.removeDrop
    };
    $[8] = waveMessagesStore.getData;
    $[9] = waveMessagesStore.removeDrop;
    $[10] = waveMessagesStore.updateData;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const waveDataManager = useWaveDataManager(t5);
  const {
    refetchAllWaves,
    resetAllWavesNewDropsCount
  } = wavesHookData;
  const {
    registerWave,
    syncNewestMessages,
    fetchNextPage,
    fetchAroundSerialNo
  } = waveDataManager;
  const wavesRef = useRef(wavesHookData.waves);
  const dmWavesRef = useRef(dmWavesHookData.waves);
  let t6;
  let t7;
  if ($[12] !== dmWavesHookData.waves || $[13] !== wavesHookData.waves) {
    t6 = () => {
      wavesRef.current = wavesHookData.waves;
      dmWavesRef.current = dmWavesHookData.waves;
    };
    t7 = [wavesHookData.waves, dmWavesHookData.waves];
    $[12] = dmWavesHookData.waves;
    $[13] = wavesHookData.waves;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = waveId => {
      const wave_1 = wavesRef.current.find(w => w.id === waveId);
      if (wave_1) {
        return wave_1.isMuted;
      }
      const dmWave = dmWavesRef.current.find(w_0 => w_0.id === waveId);
      if (dmWave) {
        return dmWave.isMuted;
      }
      return false;
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const isWaveMuted = t8;
  let t9;
  if ($[17] !== activeWaveId || $[18] !== registerWave || $[19] !== removeWaveDeliveredNotifications || $[20] !== syncNewestMessages || $[21] !== waveMessagesStore.getData || $[22] !== waveMessagesStore.removeDrop || $[23] !== waveMessagesStore.updateData) {
    t9 = {
      activeWaveId,
      getData: waveMessagesStore.getData,
      updateData: waveMessagesStore.updateData,
      registerWave,
      syncNewestMessages,
      removeDrop: waveMessagesStore.removeDrop,
      removeWaveDeliveredNotifications,
      isWaveMuted
    };
    $[17] = activeWaveId;
    $[18] = registerWave;
    $[19] = removeWaveDeliveredNotifications;
    $[20] = syncNewestMessages;
    $[21] = waveMessagesStore.getData;
    $[22] = waveMessagesStore.removeDrop;
    $[23] = waveMessagesStore.updateData;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const {
    processIncomingDrop,
    processDropRemoved
  } = useWaveRealtimeUpdater(t9);
  let t10;
  if ($[25] !== activeWaveId || $[26] !== refetchAllWaves || $[27] !== registerWave) {
    t10 = source => {
      logWebSocketDebug("Running stream sync", {
        source,
        hasActiveWave: Boolean(activeWaveId)
      });
      if (activeWaveId) {
        registerWave(activeWaveId, true);
      }
      refetchAllWaves();
    };
    $[25] = activeWaveId;
    $[26] = refetchAllWaves;
    $[27] = registerWave;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const syncActiveWaveAndRefetch = useEffectEvent(t10);
  let t11;
  if ($[29] !== activeWaveId || $[30] !== syncActiveWaveAndRefetch) {
    t11 = source_0 => {
      if (document.visibilityState !== "visible") {
        logWebSocketDebug("Browser resume sync skipped because document is hidden", {
          source: source_0,
          visibilityState: document.visibilityState
        });
        return;
      }
      const now = Date.now();
      const sinceLastBrowserResumeSyncMs = now - lastBrowserResumeSyncAtRef.current;
      if (sinceLastBrowserResumeSyncMs < BROWSER_RESUME_SYNC_COOLDOWN_MS) {
        logWebSocketDebug("Browser resume sync suppressed by cooldown", {
          source: source_0,
          sinceLastBrowserResumeSyncMs,
          cooldownMs: BROWSER_RESUME_SYNC_COOLDOWN_MS
        });
        return;
      }
      lastBrowserResumeSyncAtRef.current = now;
      logWebSocketDebug("Browser resume sync triggered", {
        source: source_0,
        hasActiveWave: Boolean(activeWaveId)
      });
      syncActiveWaveAndRefetch("browser-resume");
    };
    $[29] = activeWaveId;
    $[30] = syncActiveWaveAndRefetch;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const runBrowserResumeSync = useEffectEvent(t11);
  let t12;
  if ($[32] !== activeWaveId || $[33] !== isCapacitor || $[34] !== resetAllWavesNewDropsCount || $[35] !== syncActiveWaveAndRefetch) {
    t12 = () => {
      logWebSocketDebug("WebSocket connected; triggering stream sync", {
        hasActiveWave: Boolean(activeWaveId),
        isCapacitor
      });
      syncActiveWaveAndRefetch("websocket-connected");
      if (isCapacitor) {
        logWebSocketDebug("Resetting new drop counts after websocket reconnect on capacitor");
        resetAllWavesNewDropsCount();
      }
    };
    $[32] = activeWaveId;
    $[33] = isCapacitor;
    $[34] = resetAllWavesNewDropsCount;
    $[35] = syncActiveWaveAndRefetch;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleConnectedWebSocket = useEffectEvent(t12);
  let t13;
  if ($[37] !== activeWaveId || $[38] !== resetAllWavesNewDropsCount || $[39] !== syncActiveWaveAndRefetch) {
    t13 = () => {
      logWebSocketDebug("Capacitor app resumed; triggering stream sync", {
        hasActiveWave: Boolean(activeWaveId)
      });
      syncActiveWaveAndRefetch("capacitor-resume");
      resetAllWavesNewDropsCount();
    };
    $[37] = activeWaveId;
    $[38] = resetAllWavesNewDropsCount;
    $[39] = syncActiveWaveAndRefetch;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const handleCapacitorResume = useEffectEvent(t13);
  let t14;
  if ($[41] !== handleConnectedWebSocket || $[42] !== websocketStatus) {
    t14 = () => {
      if (websocketStatus !== WebSocketStatus.CONNECTED) {
        return;
      }
      handleConnectedWebSocket();
    };
    $[41] = handleConnectedWebSocket;
    $[42] = websocketStatus;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== websocketStatus) {
    t15 = [websocketStatus];
    $[44] = websocketStatus;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[46] !== activeWaveId || $[47] !== registerWave) {
    t16 = () => {
      if (activeWaveId) {
        registerWave(activeWaveId, true);
      }
    };
    t17 = [activeWaveId, registerWave];
    $[46] = activeWaveId;
    $[47] = registerWave;
    $[48] = t16;
    $[49] = t17;
  } else {
    t16 = $[48];
    t17 = $[49];
  }
  useEffect(t16, t17);
  let t18;
  if ($[50] !== handleCapacitorResume || $[51] !== isActive || $[52] !== isCapacitor) {
    t18 = () => {
      if (!isCapacitor) {
        return;
      }
      if (!prevIsActiveRef.current && isActive) {
        handleCapacitorResume();
      }
      prevIsActiveRef.current = isActive;
    };
    $[50] = handleCapacitorResume;
    $[51] = isActive;
    $[52] = isCapacitor;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== isActive || $[55] !== isCapacitor) {
    t19 = [isActive, isCapacitor];
    $[54] = isActive;
    $[55] = isCapacitor;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  useEffect(t18, t19);
  let t20;
  if ($[57] !== isCapacitor || $[58] !== runBrowserResumeSync) {
    t20 = () => {
      if (isCapacitor) {
        return;
      }
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        runBrowserResumeSync("visibilitychange");
      };
      const handleFocus = () => {
        runBrowserResumeSync("focus");
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("focus", handleFocus);
      };
    };
    $[57] = isCapacitor;
    $[58] = runBrowserResumeSync;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== isCapacitor) {
    t21 = [isCapacitor];
    $[60] = isCapacitor;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  useEffect(t20, t21);
  let t22;
  if ($[62] !== wavesHookData.addPinnedWave || $[63] !== wavesHookData.fetchNextPage || $[64] !== wavesHookData.hasNextPage || $[65] !== wavesHookData.isFetching || $[66] !== wavesHookData.isFetchingNextPage || $[67] !== wavesHookData.removePinnedWave || $[68] !== wavesHookData.restoreWaveUnreadCount || $[69] !== wavesHookData.waves) {
    t22 = {
      list: wavesHookData.waves,
      isFetching: wavesHookData.isFetching,
      isFetchingNextPage: wavesHookData.isFetchingNextPage,
      hasNextPage: wavesHookData.hasNextPage,
      fetchNextPage: wavesHookData.fetchNextPage,
      addPinnedWave: wavesHookData.addPinnedWave,
      removePinnedWave: wavesHookData.removePinnedWave,
      restoreWaveUnreadCount: wavesHookData.restoreWaveUnreadCount
    };
    $[62] = wavesHookData.addPinnedWave;
    $[63] = wavesHookData.fetchNextPage;
    $[64] = wavesHookData.hasNextPage;
    $[65] = wavesHookData.isFetching;
    $[66] = wavesHookData.isFetchingNextPage;
    $[67] = wavesHookData.removePinnedWave;
    $[68] = wavesHookData.restoreWaveUnreadCount;
    $[69] = wavesHookData.waves;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  const waves = t22;
  let t23;
  if ($[71] !== dmWavesHookData.addPinnedWave || $[72] !== dmWavesHookData.fetchNextPage || $[73] !== dmWavesHookData.hasNextPage || $[74] !== dmWavesHookData.isFetching || $[75] !== dmWavesHookData.isFetchingNextPage || $[76] !== dmWavesHookData.removePinnedWave || $[77] !== dmWavesHookData.restoreWaveUnreadCount || $[78] !== dmWavesHookData.waves) {
    t23 = {
      list: dmWavesHookData.waves,
      isFetching: dmWavesHookData.isFetching,
      isFetchingNextPage: dmWavesHookData.isFetchingNextPage,
      hasNextPage: dmWavesHookData.hasNextPage,
      fetchNextPage: dmWavesHookData.fetchNextPage,
      addPinnedWave: dmWavesHookData.addPinnedWave,
      removePinnedWave: dmWavesHookData.removePinnedWave,
      restoreWaveUnreadCount: dmWavesHookData.restoreWaveUnreadCount
    };
    $[71] = dmWavesHookData.addPinnedWave;
    $[72] = dmWavesHookData.fetchNextPage;
    $[73] = dmWavesHookData.hasNextPage;
    $[74] = dmWavesHookData.isFetching;
    $[75] = dmWavesHookData.isFetchingNextPage;
    $[76] = dmWavesHookData.removePinnedWave;
    $[77] = dmWavesHookData.restoreWaveUnreadCount;
    $[78] = dmWavesHookData.waves;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  const directMessages = t23;
  let t24;
  if ($[80] !== activeWaveId || $[81] !== setActiveWave) {
    t24 = {
      id: activeWaveId,
      set: setActiveWave
    };
    $[80] = activeWaveId;
    $[81] = setActiveWave;
    $[82] = t24;
  } else {
    t24 = $[82];
  }
  const activeWave = t24;
  let t25;
  if ($[83] !== waveMessagesStore.getData || $[84] !== waveMessagesStore.subscribe || $[85] !== waveMessagesStore.unsubscribe) {
    t25 = {
      getData: waveMessagesStore.getData,
      subscribe: waveMessagesStore.subscribe,
      unsubscribe: waveMessagesStore.unsubscribe
    };
    $[83] = waveMessagesStore.getData;
    $[84] = waveMessagesStore.subscribe;
    $[85] = waveMessagesStore.unsubscribe;
    $[86] = t25;
  } else {
    t25 = $[86];
  }
  const waveMessagesStoreData = t25;
  let t26;
  if ($[87] !== activeWave || $[88] !== directMessages || $[89] !== fetchAroundSerialNo || $[90] !== fetchNextPage || $[91] !== processDropRemoved || $[92] !== processIncomingDrop || $[93] !== registerWave || $[94] !== waveMessagesStore.optimisticUpdateDrop || $[95] !== waveMessagesStoreData || $[96] !== waves) {
    t26 = {
      waves,
      directMessages,
      activeWave,
      waveMessagesStore: waveMessagesStoreData,
      registerWave,
      fetchNextPageForWave: fetchNextPage,
      fetchAroundSerialNo,
      processIncomingDrop,
      processDropRemoved,
      applyOptimisticDropUpdate: waveMessagesStore.optimisticUpdateDrop
    };
    $[87] = activeWave;
    $[88] = directMessages;
    $[89] = fetchAroundSerialNo;
    $[90] = fetchNextPage;
    $[91] = processDropRemoved;
    $[92] = processIncomingDrop;
    $[93] = registerWave;
    $[94] = waveMessagesStore.optimisticUpdateDrop;
    $[95] = waveMessagesStoreData;
    $[96] = waves;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  const contextValue = t26;
  let t27;
  if ($[98] !== children || $[99] !== contextValue) {
    t27 = <MyStreamContext.Provider value={contextValue}>{children}</MyStreamContext.Provider>;
    $[98] = children;
    $[99] = contextValue;
    $[100] = t27;
  } else {
    t27 = $[100];
  }
  return t27;
};

// Create a custom hook for using this context
export const useMyStream = () => {
  const context = useContext(MyStreamContext);
  if (!context) {
    throw new Error("useMyStream must be used within a MyStreamProvider");
  }
  return context;
};
export const useMyStreamOptional = () => {
  return useContext(MyStreamContext);
};

// Create the selector hook for wave messages
export function useMyStreamWaveMessages(waveId) {
  const $ = _c(12);
  const {
    waveMessagesStore
  } = useMyStream();
  const {
    getData,
    subscribe,
    unsubscribe
  } = waveMessagesStore;
  let t0;
  if ($[0] !== getData || $[1] !== waveId) {
    t0 = () => waveId ? getData(waveId) : undefined;
    $[0] = getData;
    $[1] = waveId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [data, setData] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== subscribe || $[4] !== unsubscribe || $[5] !== waveId) {
    t1 = () => {
      if (!waveId) {
        setData(undefined);
        return;
      }
      const listener = newData => {
        setData(currentData => {
          if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
            return newData;
          }
          return currentData;
        });
      };
      subscribe(waveId, listener);
      return () => {
        unsubscribe(waveId, listener);
      };
    };
    t2 = [waveId, subscribe, unsubscribe];
    $[3] = subscribe;
    $[4] = unsubscribe;
    $[5] = waveId;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== getData || $[9] !== waveId) {
    t3 = () => {
      setData(waveId ? getData(waveId) : undefined);
    };
    t4 = [waveId, getData];
    $[8] = getData;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  return data;
}
function _temp(wave) {
  return wave.id;
}
function _temp2(wave_0) {
  return wave_0.id;
}